import React, { MouseEventHandler, ReactNode } from 'react';

import { Modal, ModalProps } from '@mui/material';
import { Button, Spinner } from '@infoods/lifebase-ui';

import classes from './LbModal.module.scss';

type ButtonConfig = {
  onClick: MouseEventHandler<HTMLButtonElement>;
  startIcon?: ReactNode;
  disabled?: boolean;
  text?: string;
};

type PrimaryBtnConfig = Omit<ButtonConfig, 'onClick'> &
  (
    | { onClick?: never; formId: string }
    | { onClick: MouseEventHandler<HTMLButtonElement>; formId?: never }
  );

type LbModalProps = {
  title?: string | ReactNode;
  text?: string;
  icon?: ReactNode;
  isLoading?: boolean;
  renderBtnBox?: ReactNode;
  primaryBtnConfig?: PrimaryBtnConfig;
  secondaryBtnConfig?: ButtonConfig;
  children?: ReactNode;
} & Pick<ModalProps, 'open' | 'onClose'>;

/**
 * @description Each button renders only when configured: the secondary when its `onClick` is passed,
 * the primary when `onClick` or `formId` is passed. `formId` makes the primary a submit button for a
 * form rendered elsewhere (e.g. in the modal body).
 * Default text is 'Yes' and 'Cancel'.
 */
const LbModal = ({
  open,
  title,
  text,
  icon,
  children,
  isLoading = false,
  renderBtnBox,
  onClose,
  primaryBtnConfig,
  secondaryBtnConfig,
}: LbModalProps) => {
  const { text: secondaryText, ...secondaryBtnProps } = secondaryBtnConfig || {};
  const { text: primaryText, formId, ...primaryBtnProps } = primaryBtnConfig || {};

  const isShowPrimaryBtn = !!primaryBtnConfig?.onClick || formId;

  const defaultBtnBox = (
    <div className={classes.buttonBox}>
      {!!secondaryBtnConfig?.onClick && (
        <Button variant="outlined" size="medium" {...secondaryBtnProps}>
          {secondaryText || 'Cancel'}
        </Button>
      )}
      {isShowPrimaryBtn && (
        <Button
          variant="contained"
          size="medium"
          type="submit"
          form={formId}
          {...primaryBtnProps}
        >
          {primaryText || 'Yes'}
        </Button>
      )}
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} data-test-id={testIds.modalBox}>
      <div className={classes.modalPaper}>
        {icon}
        <div className={classes.modalText}>
          <h2 className={classes.modalTitle}>{title}</h2>
          {text && <p>{text}</p>}
        </div>
        {!!children && <div>{children}</div>}
        {renderBtnBox || defaultBtnBox}
        <Spinner isLoading={isLoading} />
      </div>
    </Modal>
  );
};

const testIds = { modalBox: 'modalBox' };

export { LbModal, testIds, LbModalProps, ButtonConfig };
